import { Alert, ChoiceCard, ChoiceGroup, RadioGroup, Stack, t } from '@kanbios/ui';
import { useSession } from '../../lib/session';

export interface QualificationDraft {
  criticality: string | null;
  healthData: boolean | null;
  dora: boolean | null;
  sensitivePersonalData: boolean | null;
  internetExposed: boolean | null;
}

export const QUESTIONS = ['healthData', 'dora', 'sensitivePersonalData', 'internetExposed'] as const;
export const qualificationComplete = (d: QualificationDraft) => d.criticality !== null && QUESTIONS.every(q => d[q] !== null);

/**
 * Criticality and regulatory qualification (SPEC-006 RG-006-1): an explicit
 * answer to each question, no default; health data and DORA say their
 * consequence at once. Shared by the Nouveau projet assistant and the
 * « Modifier la qualification » dialog.
 */
export function QualificationFields({ value, onChange, errors = false }: { value: QualificationDraft; onChange: (v: QualificationDraft) => void; errors?: boolean }) {
  const { enums } = useSession();
  const yesNo = [{ value: 'yes', label: t('qualification.yes') }, { value: 'no', label: t('qualification.no') }];
  const asRadio = (v: boolean | null) => (v === null ? undefined : v ? 'yes' : 'no');
  return (
    <Stack gap={24}>
      <ChoiceGroup label={t('qualification.criticality')}>
        {enums.criticalities.map(c => (
          <ChoiceCard key={c.value} variant="radio" title={c.label} description={c.description}
            selected={value.criticality === c.value} onClick={() => onChange({ ...value, criticality: c.value })} />
        ))}
      </ChoiceGroup>
      {QUESTIONS.map(q => (
        <RadioGroup
          key={q}
          inline
          label={t(`qualification.questions.${q}`)}
          options={yesNo}
          value={asRadio(value[q])}
          onChange={v => onChange({ ...value, [q]: v === 'yes' })}
          error={errors && value[q] === null ? t('qualification.required') : undefined}
        />
      ))}
      {value.healthData && <Alert tone="neg">{t('qualification.healthAlert')}</Alert>}
      {value.dora && <Alert tone="info">{t('qualification.doraAlert')}</Alert>}
    </Stack>
  );
}
